'use client';

import { useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { Header } from '@/components/layout/header';
import { Footer } from '@/components/layout/footer';
import { PageHero } from '@/components/sections/page-hero';
import { Button } from '@/components/ui/button';
import { Link } from '@/i18n/navigation';

/**
 * Themed error boundary for this locale segment. Client component per the
 * App Router error.js contract (Next.js requires it, since recovery — the
 * `reset()` call — has to happen in the browser).
 *
 * Only ever rendered for errors thrown by a page or nested layout below the
 * [locale] layout — a segment's own layout.tsx errors are not caught by its
 * own error.js, they bubble further up — so Header/Footer and the
 * NextIntlClientProvider are guaranteed to already be mounted here.
 */
export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const t = useTranslations('error');

  useEffect(() => {
    console.error(error);
  }, [error]);

  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main className="flex-1">
        <PageHero overline={t('overline')} heading={t('heading')} subheading={t('description')}>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Button size="lg" onClick={reset}>
              {t('retry')}
            </Button>
            <Button asChild size="lg" variant="outline">
              <Link href="/">{t('cta')}</Link>
            </Button>
          </div>
        </PageHero>
      </main>
      <Footer />
    </div>
  );
}
